import type {
  GameConfig,
  GameState,
  PlayerConfig,
  PlayerState,
} from '@/engine/types';
import { chooseAiAction } from '@/engine/ai';
import { boxOptions } from '@/engine/reducer';
import { mulberry32, shortId } from '@/engine/rng';
import { isVariantId } from '@/engine/variants';
import { useGame } from '@/store/game';
import { PLAYER_COLORS, type PlayerColorId } from '@/lib/options';
import {
  MAX_REMOTE_GUESTS,
  msg,
  parseMessage,
  randomCode,
  stateMessage,
  type Message,
  type RemoteSeat,
} from './protocol';
import { useRoomStore, type ConnectionDiag, type JoinStage } from './roomStore';
import {
  TransportError,
  type Conn,
  type Transport,
  type TransportEvent,
} from './transport';

const PING_INTERVAL_MS = 10_000;
/** Phones throttle timers hard in the background; the relay link has its own keepalive. */
const PONG_TIMEOUT_MS = 45_000;
/** A guest who drops out of the lobby keeps their seat this long. */
export const LOBBY_GRACE_MS = 60_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 10_000;
/** Memos older than this are ignored (the relay forgets idle rooms after 24 h). */
export const MEMO_TTL_MS = 24 * 60 * 60 * 1000;
const GUEST_MEMO_KEY = 'yazzi.room.guest.v1';
const HOST_MEMO_KEY = 'yazzi.room.host.v1';
/** Cap on AI steps when the host plays a turn for an absent seat. */
const MAX_AI_STEPS = 40;
/** A guest waiting for the host to answer its hello re-sends after this long. */
export const HELLO_TIMEOUT_MS = 10_000;

export const HINT_RELAY_UNREACHABLE =
  "Can't reach the relay. Check your connection.";
export const HINT_HOST_AWAY =
  "The host is away. You'll join as soon as they're back.";
export const HINT_HOST_SILENT = "The host isn't responding. Still trying…";
export const HINT_MOVE_UNACKED =
  "Your move hasn't reached the host yet. Checking the connection…";
/** How long a sent action may go unanswered before we suspect a dead socket. */
export const ACK_TIMEOUT_MS = 4_000;

/** Mirror transport diagnostics into the room store for the Connection sheet. */
export function wireDiag(transport: Transport, relayUrl: string): void {
  const patch = (p: Partial<ConnectionDiag>) => {
    const s = useRoomStore.getState();
    s.patch({ diag: { ...s.diag, ...p } });
  };
  patch({ relayUrl });
  transport.onEvent?.((e: TransportEvent) => {
    const now = Date.now();
    switch (e.type) {
      case 'attached':
        patch({ epoch: e.epoch, lastError: null });
        break;
      case 'inbound':
        patch({ lastInboundAt: now });
        break;
      case 'outbound':
        patch({ lastOutboundAt: now });
        break;
      case 'pong':
        patch({ pingMs: e.rttMs, lastInboundAt: now });
        break;
      case 'reconnect':
        patch({ reconnects: useRoomStore.getState().diag.reconnects + 1 });
        break;
      case 'error':
        patch({ lastError: e.message });
        break;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Memos (localStorage)                                                */
/* ------------------------------------------------------------------ */

export interface GuestMemo {
  code: string;
  playerId: string;
  name: string;
  color: PlayerColorId;
  joinedAt: number;
}

export interface HostMemo {
  code: string;
  token: string;
  hostName: string;
  /** Player ids of remote guests in the running game. */
  remoteIds: string[];
  openedAt: number;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown | null): void {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode */
  }
}

function fresh(at: number, now = Date.now()): boolean {
  return now - at < MEMO_TTL_MS;
}

export function readGuestMemo(): GuestMemo | null {
  const m = readJson<GuestMemo>(GUEST_MEMO_KEY);
  if (!m || !fresh(m.joinedAt)) return null;
  return m;
}

export function writeGuestMemo(memo: GuestMemo | null): void {
  writeJson(GUEST_MEMO_KEY, memo);
}

export function readHostMemo(): HostMemo | null {
  const m = readJson<HostMemo>(HOST_MEMO_KEY);
  if (!m || !fresh(m.openedAt)) return null;
  return m;
}

export function writeHostMemo(memo: HostMemo | null): void {
  writeJson(HOST_MEMO_KEY, memo);
}

export interface GuestProfile {
  name: string;
  color: PlayerColorId;
}

/* ------------------------------------------------------------------ */
/* Host                                                                */
/* ------------------------------------------------------------------ */

interface Guest {
  seat: RemoteSeat;
  conn: Conn | null;
  lastPong: number;
  /** Lobby only: pending removal after a drop. */
  graceTimer: ReturnType<typeof setTimeout> | null;
}

export interface HostRoomOptions {
  /** Reopen an existing room with its token. */
  token?: string | null;
  /** Seats to seed when reopening (from the persisted game). */
  seats?: RemoteSeat[];
  now?: () => number;
}

export class HostRoom {
  readonly code: string;
  token: string | null;
  private guests = new Map<string, Guest>(); // playerId -> guest
  private connOwner = new Map<Conn, string>(); // conn -> playerId
  private unsubscribeGame: (() => void) | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastBroadcastVersion = -1;
  private lastGameId: string | null = null;
  private closed = false;
  private openedAt: number;
  private everConnected = false;

  private readonly transport: Transport;
  private readonly hostName: string;
  private readonly getVariantId: () => string | null;
  private readonly now: () => number;

  constructor(
    transport: Transport,
    hostName: string,
    getVariantId: () => string | null,
    code?: string,
    opts: HostRoomOptions = {}
  ) {
    this.transport = transport;
    this.hostName = hostName;
    this.getVariantId = getVariantId;
    this.now = opts.now ?? (() => Date.now());
    this.code = code ?? randomCode();
    this.token = opts.token ?? null;
    this.openedAt = this.now();
    for (const seat of opts.seats ?? []) {
      this.guests.set(seat.playerId, {
        seat: { ...seat, status: 'disconnected' },
        conn: null,
        lastPong: this.now(),
        graceTimer: null,
      });
    }
  }

  async open(): Promise<void> {
    this.transport.onStatus?.(status => {
      if (this.closed) return;
      useRoomStore.getState().patch({ connection: status });
      if (status === 'connected') {
        // Nobody could pong while we were away; do not punish them for it.
        const now = this.now();
        for (const g of this.guests.values()) g.lastPong = now;
        if (this.everConnected) this.onReattached();
        this.everConnected = true;
      }
    });
    const { token } = await this.transport.listen(
      this.code,
      conn => this.accept(conn),
      this.token
    );
    this.token = token;
    useRoomStore.getState().patch({
      status: this.inGame() ? 'playing' : 'hosting',
      role: 'host',
      code: this.code,
      hostName: this.hostName,
      seats: this.seats,
      error: null,
      connection: 'connected',
      hostPresent: true,
      blockedNotice: null,
    });
    this.persist();
    this.pingTimer = setInterval(() => this.pingAll(), PING_INTERVAL_MS);
  }

  /**
   * The relay link came back after a drop: every guest socket the relay still
   * has is re-announced through `peers`, but their seats are only trusted
   * again once they re-hello. Ask them to, and push the current state so
   * nobody is left looking at a stale board.
   */
  onReattached(): void {
    this.broadcast(msg('who', {}));
    this.resendState();
    this.syncStore();
  }

  private resendState(): void {
    const g = this.game;
    if (g && this.inGame()) this.broadcast(stateMessage('state', g));
  }

  /** Attach a connection (public for tests). */
  accept(conn: Conn): void {
    if (this.closed) {
      conn.close();
      return;
    }
    conn.onData(raw => this.onData(conn, raw));
    conn.onClose(() => this.onClose(conn));
    // Every fresh conn is asked to identify itself. New guests hello anyway;
    // returning guests (after either side dropped) reclaim their seat.
    conn.send(msg('who', {}));
  }

  get seats(): RemoteSeat[] {
    return [...this.guests.values()].map(g => g.seat);
  }

  get remoteIds(): string[] {
    return [...this.guests.keys()];
  }

  private get game(): GameState | null {
    return useGame.getState().game;
  }

  /** A game involving this room's guests is running (seats may be AI-held). */
  private inGame(): boolean {
    const g = this.game;
    return !!g && g.players.some(p => this.guests.has(p.id));
  }

  private persist(): void {
    if (!this.token) return;
    writeHostMemo({
      code: this.code,
      token: this.token,
      hostName: this.hostName,
      remoteIds: this.remoteIds,
      openedAt: this.openedAt,
    });
  }

  private onData(conn: Conn, raw: unknown): void {
    const m = parseMessage(raw);
    if (!m) return;
    const owner = this.connOwner.get(conn);

    if (m.t === 'hello') {
      if (owner) {
        // Guests re-hello whenever the host reappears; refresh their view.
        const guest = this.guests.get(owner);
        if (guest) {
          guest.lastPong = this.now();
          conn.send(msg('assign', { playerId: owner }));
          this.sendLobbyTo(conn);
          const g = this.game;
          if (g && this.inGame()) conn.send(stateMessage('state', g));
        }
        return;
      }
      this.onHello(conn, m);
      return;
    }
    if (!owner) return; // ignore anything before a valid hello
    const guest = this.guests.get(owner);
    if (!guest || guest.conn !== conn) return;

    switch (m.t) {
      case 'pong':
        guest.lastPong = this.now();
        break;
      case 'ping':
        conn.send(msg('pong', {}));
        break;
      case 'action':
        this.onAction(owner, m);
        break;
      case 'chat':
        this.broadcast(msg('chat', { from: guest.seat.name, text: m.text }));
        break;
      default:
        break; // clients never send lobby/state/start/assign/kick
    }
  }

  private onHello(conn: Conn, m: Extract<Message, { t: 'hello' }>): void {
    // Reclaim an existing seat after a reconnect or a reload.
    const existing = m.playerId ? this.guests.get(m.playerId) : undefined;
    if (existing) {
      if (existing.graceTimer) {
        clearTimeout(existing.graceTimer);
        existing.graceTimer = null;
      }
      if (existing.conn && existing.conn !== conn) {
        this.connOwner.delete(existing.conn);
        existing.conn.close();
      }
      existing.conn = conn;
      existing.lastPong = this.now();
      existing.seat = { ...existing.seat, status: 'connected' };
      this.connOwner.set(conn, existing.seat.playerId);
      conn.send(msg('assign', { playerId: existing.seat.playerId }));
      // A seat handed to the computer goes back to its owner.
      const store = useGame.getState();
      const player = store.game?.players.find(
        p => p.id === existing.seat.playerId
      );
      if (player && player.kind === 'ai') {
        store.apply({
          type: 'setPlayerKind',
          playerId: player.id,
          kind: 'remote',
        });
      }
      this.syncStore();
      this.sendLobby();
      const g = this.game;
      if (g && this.inGame()) conn.send(stateMessage('state', g));
      return;
    }

    if (this.inGame()) {
      this.reject(conn, 'That game has already started.');
      return;
    }
    if (this.guests.size >= MAX_REMOTE_GUESTS) {
      this.reject(conn, 'That room is full.');
      return;
    }

    const playerId = shortId(6);
    const seat: RemoteSeat = {
      playerId,
      name: this.uniqueName(m.name),
      color: this.freeColor(m.color),
      status: 'connected',
    };
    this.guests.set(playerId, {
      seat,
      conn,
      lastPong: this.now(),
      graceTimer: null,
    });
    this.connOwner.set(conn, playerId);
    conn.send(msg('assign', { playerId }));
    this.syncStore();
    this.sendLobby();
  }

  private reject(conn: Conn, reason: string): void {
    conn.send(msg('kick', { reason }));
    conn.close();
  }

  private uniqueName(name: string): string {
    const taken = new Set([this.hostName, ...this.seats.map(s => s.name)]);
    if (!taken.has(name)) return name;
    let n = 2;
    while (taken.has(`${name} ${n}`)) n++;
    return `${name} ${n}`;
  }

  private freeColor(wanted: PlayerColorId): PlayerColorId {
    const taken = new Set(this.seats.map(s => s.color));
    if (!taken.has(wanted)) return wanted;
    return PLAYER_COLORS.find(c => !taken.has(c.id))?.id ?? wanted;
  }

  private onAction(
    playerId: string,
    m: Extract<Message, { t: 'action' }>
  ): void {
    if (m.playerId !== playerId) return; // spoofed
    const store = useGame.getState();
    const g = store.game;
    if (!g || g.phase !== 'playing') return;
    if (m.action.type === 'undoScore') {
      // Only the player who scored may take it back, whoever's turn it is now.
      const scorer = g.players.find(p => p.id === g.scoreUndo?.playerId);
      if (scorer?.id === playerId && scorer.kind === 'remote')
        store.apply(m.action);
      return;
    }
    const current = g.players[g.turn];
    if (!current || current.id !== playerId || current.kind !== 'remote')
      return;
    store.apply(m.action);
    // The game subscription broadcasts the new state.
  }

  private onClose(conn: Conn): void {
    const playerId = this.connOwner.get(conn);
    this.connOwner.delete(conn);
    if (!playerId) return;
    const guest = this.guests.get(playerId);
    if (!guest || guest.conn !== conn) return;
    guest.conn = null;
    guest.seat = { ...guest.seat, status: 'disconnected' };
    if (!this.inGame() && !guest.graceTimer) {
      // Before the game starts, a dropped guest keeps the seat for a while
      // (a locked phone should not lose its place in the lobby).
      guest.graceTimer = setTimeout(() => {
        guest.graceTimer = null;
        if (guest.conn || this.closed) return;
        this.guests.delete(playerId);
        this.syncStore();
        this.sendLobby();
      }, LOBBY_GRACE_MS);
    }
    this.syncStore();
    this.sendLobby();
  }

  private pingAll(): void {
    const now = this.now();
    for (const guest of this.guests.values()) {
      if (!guest.conn) continue;
      if (now - guest.lastPong > PONG_TIMEOUT_MS) {
        const c = guest.conn;
        c.close();
        this.onClose(c);
        continue;
      }
      guest.conn.send(msg('ping', {}));
    }
  }

  private syncStore(): void {
    useRoomStore.getState().patch({ seats: this.seats });
  }

  private lobbyMessage(): Message {
    return msg('lobby', {
      hostName: this.hostName,
      variantId: this.getVariantId(),
      seats: this.seats,
      inGame: this.inGame(),
    });
  }

  private sendLobby(): void {
    this.broadcast(this.lobbyMessage());
  }

  private sendLobbyTo(conn: Conn): void {
    conn.send(this.lobbyMessage());
  }

  broadcast(message: Message): void {
    for (const guest of this.guests.values()) guest.conn?.send(message);
  }

  /** Remote guests as players, in join order. */
  remotePlayers(): PlayerConfig[] {
    return this.seats.map(s => ({
      id: s.playerId,
      name: s.name,
      color: s.color,
      kind: 'remote' as const,
    }));
  }

  /**
   * Start a game. `config.players` should be the host's local seats followed
   * by `remotePlayers()`; remote seats are appended automatically if missing.
   */
  startGame(config: GameConfig): GameState {
    const remote = this.remotePlayers();
    const present = new Set(config.players.map(p => p.id));
    const players = [
      ...config.players,
      ...remote.filter(r => !present.has(r.id)),
    ];
    const store = useGame.getState();
    store.setInterceptor(null);
    const localIds = players.filter(p => p.kind === 'human').map(p => p.id);
    const game = store.start({ ...config, players });
    store.setRole('host', localIds);
    this.watchGame();
    this.lastGameId = game.id;
    this.lastBroadcastVersion = game.version;
    this.broadcast(stateMessage('start', game));
    useRoomStore.getState().patch({ status: 'playing' });
    this.persist();
    return game;
  }

  /**
   * Resume hosting a persisted game (after a reload). Guests reclaim their
   * seats with `hello` as they reconnect.
   */
  resumeGame(game: GameState): void {
    const store = useGame.getState();
    store.setInterceptor(null);
    const localIds = game.players
      .filter(p => p.kind === 'human')
      .map(p => p.id);
    store.setRole('host', localIds);
    this.lastGameId = game.id;
    this.lastBroadcastVersion = game.version;
    this.watchGame();
    useRoomStore.getState().patch({ status: 'playing' });
    this.persist();
    // Guests that stayed attached while we were gone reattach without a refresh.
    this.broadcast(msg('who', {}));
    this.broadcast(stateMessage('state', game));
  }

  private watchGame(): void {
    if (this.unsubscribeGame) return;
    this.unsubscribeGame = useGame.subscribe(state => {
      const g = state.game;
      if (!g) return;
      if (g.id !== this.lastGameId) {
        // A new game (e.g. "Play again") started via the store.
        this.lastGameId = g.id;
        this.lastBroadcastVersion = g.version;
        this.broadcast(stateMessage('start', g));
        this.persist();
        return;
      }
      if (g.version === this.lastBroadcastVersion) return;
      this.lastBroadcastVersion = g.version;
      this.broadcast(stateMessage('state', g));
      if (g.phase === 'finished') writeHostMemo(null);
    });
  }

  /** The seat's current turn is played by the computer, once. */
  playTurnForSeat(playerId: string): void {
    const store = useGame.getState();
    const g = store.game;
    if (!g || g.phase !== 'playing') return;
    const seat = g.players[g.turn];
    if (!seat || seat.id !== playerId || !this.guests.has(playerId)) return;
    const rng = mulberry32((g.seed ^ g.version) >>> 0);
    for (let i = 0; i < MAX_AI_STEPS; i++) {
      const cur = useGame.getState().game;
      if (!cur || cur.phase !== 'playing' || cur.turn !== g.turn) return;
      if (cur.round !== g.round) return;
      const before = cur.version;
      store.apply(chooseAiAction(cur, seat.aiLevel ?? 'normal', rng));
      const after = useGame.getState().game;
      if (!after || after.version === before) {
        // The AI's suggestion was rejected; fall back to the best legal box.
        const rolled = useGame.getState().game;
        if (rolled && rolled.rollsUsed === 0) store.apply({ type: 'roll' });
        else break;
      }
    }
    // Last resort: scratch the best legal box so the game cannot stall.
    const cur = useGame.getState().game;
    if (cur && cur.phase === 'playing' && cur.turn === g.turn) {
      const legal = boxOptions(cur)
        .filter(o => o.legal)
        .sort((a, b) => b.score - a.score)[0];
      if (legal)
        store.apply({
          type: 'score',
          categoryId: legal.categoryId,
          col: legal.col,
        });
    }
  }

  /** Hand a remote seat to the computer for the rest of the game (until they return). */
  handSeatToAi(playerId: string): void {
    if (!this.guests.has(playerId)) return;
    const store = useGame.getState();
    const player = store.game?.players.find(p => p.id === playerId);
    if (!player || player.kind !== 'remote') return;
    store.apply({
      type: 'setPlayerKind',
      playerId,
      kind: 'ai',
      aiLevel: 'normal',
    });
  }

  /** Close the room. `forget` also drops the persisted memo (user left for good). */
  close(forget = true): void {
    if (this.closed) return;
    this.closed = true;
    if (forget) {
      // Leaving for good: tell guests and tombstone the room on the relay.
      this.broadcast(msg('ended', {}));
      this.transport.end?.();
    }
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.unsubscribeGame?.();
    for (const guest of this.guests.values()) {
      if (guest.graceTimer) clearTimeout(guest.graceTimer);
      guest.conn?.close();
    }
    this.guests.clear();
    this.connOwner.clear();
    this.transport.destroy();
    if (forget) writeHostMemo(null);
    useRoomStore.getState().reset();
  }
}

/* ------------------------------------------------------------------ */
/* Client                                                              */
/* ------------------------------------------------------------------ */

export class ClientRoom {
  private conn: Conn | null = null;
  private playerId: string | null = null;
  private closed = false;
  private reconnecting = false;
  private hostPresent = true;
  private wake: (() => void) | null = null;
  private stage: JoinStage = 'idle';
  private helloTimer: ReturnType<typeof setTimeout> | null = null;
  /** Fires if the host has not moved past our last action's version. */
  private ackTimer: ReturnType<typeof setTimeout> | null = null;
  /** In-flight connect, so a repeated join for the same room reuses it. */
  pending: Promise<void> | null = null;

  private readonly transport: Transport;
  readonly code: string;
  readonly profile: GuestProfile;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly onVisible = () => {
    if (
      typeof document === 'undefined' ||
      document.visibilityState === 'visible'
    ) {
      this.transport.probe?.();
      this.wake?.();
    }
  };

  constructor(
    transport: Transport,
    code: string,
    profile: GuestProfile,
    opts: {
      /** Seat to reclaim (from a memo), if any. */
      playerId?: string | null;
      sleep?: (ms: number) => Promise<void>;
    } = {}
  ) {
    this.transport = transport;
    this.code = code;
    this.profile = profile;
    this.playerId = opts.playerId ?? null;
    this.sleep =
      opts.sleep ??
      (ms =>
        new Promise(resolve => {
          let id: ReturnType<typeof setTimeout> | null = null;
          const done = () => {
            if (id) clearTimeout(id);
            this.wake = null;
            resolve();
          };
          id = setTimeout(done, ms);
          this.wake = done;
        }));
    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisible);
      window.addEventListener('online', this.onVisible);
      window.addEventListener('pageshow', this.onVisible);
    }
  }

  /**
   * Join. Resolves once attached to the relay and the hello is on its way.
   * A relay that cannot be reached is not fatal: the reconnect loop keeps
   * trying with a visible hint. Only a room that is gone, ended, or refused
   * rejects.
   */
  async connect(): Promise<void> {
    useRoomStore.getState().patch({
      status: 'connecting',
      role: 'client',
      code: this.code,
      error: null,
      connection: 'reconnecting',
    });
    this.setStage('relay', null);
    this.transport.onStatus?.(status => {
      if (!this.closed && status === 'connected')
        useRoomStore.getState().patch({ connection: 'connected' });
    });
    try {
      const conn = await this.transport.connect(this.code);
      this.attach(conn);
    } catch (err) {
      if (err instanceof TransportError && err.kind === 'network') {
        this.setStage('relay', HINT_RELAY_UNREACHABLE);
        void this.reconnectLoop();
        return;
      }
      throw err;
    }
  }

  private setStage(stage: JoinStage, hint: string | null): void {
    this.stage = stage;
    useRoomStore.getState().patch({ joinStage: stage, joinHint: hint });
  }

  /** While waiting for the host's answer, say so and keep re-sending hello. */
  private armHelloTimeout(): void {
    this.clearHelloTimeout();
    this.helloTimer = setTimeout(() => {
      this.helloTimer = null;
      if (this.closed || this.stage !== 'host') return;
      this.setStage(
        'host',
        this.hostPresent ? HINT_HOST_SILENT : HINT_HOST_AWAY
      );
      this.sendHello();
      this.armHelloTimeout();
    }, HELLO_TIMEOUT_MS);
  }

  private clearHelloTimeout(): void {
    if (this.helloTimer) clearTimeout(this.helloTimer);
    this.helloTimer = null;
    if (this.ackTimer) clearTimeout(this.ackTimer);
    this.ackTimer = null;
  }

  /** Bind a connection (public for tests). */
  attach(conn: Conn): void {
    this.conn = conn;
    conn.onData(raw => this.onData(raw));
    conn.onClose(() => void this.onClose(conn));
    let first = true;
    conn.onHostPresence?.(present => {
      const was = this.hostPresent;
      this.hostPresent = present;
      useRoomStore.getState().patch({
        hostPresent: present,
        ...(present ? { blockedNotice: null } : {}),
      });
      if (this.stage === 'host') {
        this.setStage('host', present ? null : HINT_HOST_AWAY);
      }
      if (first) {
        first = false;
        return;
      }
      // The host came back (reload or blip): reclaim our seat.
      if (present && !was) this.sendHello();
    });
    useRoomStore.getState().patch({ connection: 'connected' });
    this.setStage('host', this.hostPresent ? null : HINT_HOST_AWAY);
    this.sendHello();
  }

  private sendHello(): void {
    this.conn?.send(
      msg('hello', {
        name: this.profile.name,
        color: this.profile.color,
        playerId: this.playerId ?? undefined,
      })
    );
    this.armHelloTimeout();
  }

  private rememberSeat(playerId: string): void {
    writeGuestMemo({
      code: this.code,
      playerId,
      name: this.profile.name,
      color: this.profile.color,
      joinedAt: Date.now(),
    });
  }

  private onData(raw: unknown): void {
    const m = parseMessage(raw);
    if (!m) return;
    const room = useRoomStore.getState();
    switch (m.t) {
      case 'assign': {
        this.playerId = m.playerId;
        this.rememberSeat(m.playerId);
        this.clearHelloTimeout();
        room.patch({
          selfPlayerId: m.playerId,
          status: room.status === 'playing' ? 'playing' : 'waiting',
          error: null,
          connection: 'connected',
        });
        this.setStage('seat', null);
        this.installInterceptor();
        break;
      }
      case 'lobby':
        room.patch({
          hostName: m.hostName,
          variantId:
            m.variantId && isVariantId(m.variantId) ? m.variantId : null,
          seats: m.seats,
          status: room.status === 'playing' ? 'playing' : 'waiting',
        });
        // In the lobby there is no state to wait for: we are in.
        if (!m.inGame && this.stage === 'seat') this.setStage('done', null);
        break;
      case 'who':
        // The host (re)attached and wants everyone to reclaim their seats.
        this.sendHello();
        break;
      case 'start':
      case 'state': {
        const store = useGame.getState();
        const game = m.game as unknown as GameState;
        store.setGame(game);
        if (this.playerId) store.setRole('client', [this.playerId]);
        this.installInterceptor();
        if (room.status !== 'playing') room.patch({ status: 'playing' });
        if (game.phase === 'finished') writeGuestMemo(null);
        else if (this.playerId) this.rememberSeat(this.playerId);
        if (this.stage !== 'host') this.setStage('done', null);
        break;
      }
      case 'ping':
        this.conn?.send(msg('pong', {}));
        break;
      case 'kick':
        this.fail(m.reason, true);
        break;
      case 'ended':
        this.closed = true;
        this.clearHelloTimeout();
        this.teardownGame();
        writeGuestMemo(null);
        room.patch({
          status: 'ended',
          error: 'The host closed the room.',
          connection: 'lost',
          joinStage: 'idle',
          joinHint: null,
        });
        this.transport.destroy();
        this.removeListeners();
        break;
      default:
        break;
    }
  }

  private installInterceptor(): void {
    const store = useGame.getState();
    store.setInterceptor(action => {
      const g = useGame.getState().game;
      const pid = this.playerId;
      if (!g || !pid) return true;
      const current = g.players[g.turn];
      if (current?.id !== pid) return true;
      if (!this.conn) {
        useRoomStore
          .getState()
          .patch({ blockedNotice: 'Reconnecting to the room…' });
        return true;
      }
      if (!this.hostPresent) {
        useRoomStore.getState().patch({
          blockedNotice: 'The host is away. Your move will wait for them.',
        });
        return true;
      }
      this.conn.send(msg('action', { action, playerId: pid }));
      if (useRoomStore.getState().blockedNotice)
        useRoomStore.getState().patch({ blockedNotice: null });
      this.expectAck(g.version);
      return true; // never apply locally; the host's state is the truth
    });
  }

  /**
   * A socket the browser still believes is open may already be dead (phone
   * came back from the background). If the host does not answer an action
   * within a few seconds, say so and probe the link.
   */
  private expectAck(version: number): void {
    if (this.ackTimer) clearTimeout(this.ackTimer);
    this.ackTimer = setTimeout(() => {
      this.ackTimer = null;
      if (this.closed) return;
      const g = useGame.getState().game;
      if (!g || g.version !== version) return; // the host moved on: acked
      useRoomStore.getState().patch({ blockedNotice: HINT_MOVE_UNACKED });
      this.transport.probe?.();
    }, ACK_TIMEOUT_MS);
  }

  private async onClose(conn: Conn): Promise<void> {
    if (this.closed || conn !== this.conn) return;
    this.conn = null;
    this.clearHelloTimeout();
    void this.reconnectLoop();
  }

  /**
   * Reconnect forever with capped backoff. A room that is confirmed gone or
   * ended is the only way out (besides the user leaving).
   */
  private async reconnectLoop(): Promise<void> {
    if (this.reconnecting || this.closed) return;
    this.reconnecting = true;
    useRoomStore.getState().patch({
      status: 'connecting',
      connection: 'reconnecting',
      error: null,
    });
    this.setStage('relay', useRoomStore.getState().joinHint);
    let delay = BACKOFF_MIN_MS;
    let attempts = 0;
    while (!this.closed) {
      await this.sleep(delay);
      if (this.closed) return;
      try {
        const next = await this.transport.connect(this.code);
        this.reconnecting = false;
        this.attach(next);
        return;
      } catch (err) {
        attempts += 1;
        const kind = err instanceof TransportError ? err.kind : 'unknown';
        if (kind === 'ended') {
          this.reconnecting = false;
          this.fail('The host closed the room.', true);
          return;
        }
        if (kind === 'peer-unavailable') {
          // Do not forget a seat on a single refusal: ask the relay first.
          const probe = await this.probe();
          if (probe && probe.exists && !probe.ended) {
            this.setStage('relay', HINT_RELAY_UNREACHABLE);
          } else {
            this.reconnecting = false;
            this.fail('That room is no longer open.', true);
            return;
          }
        } else if (kind === 'id-taken') {
          this.reconnecting = false;
          this.fail('That room refused the connection.', false);
          return;
        } else if (attempts >= 1) {
          this.setStage('relay', HINT_RELAY_UNREACHABLE);
        }
        delay = Math.min(delay * 2, BACKOFF_MAX_MS);
      }
    }
  }

  /** Hook for the session layer to answer "does this room still exist?". */
  probe: () => Promise<{ exists: boolean; ended: boolean } | null> = async () =>
    null;

  /** Retry immediately (visibility/online events or a user tap). */
  retryNow(): void {
    this.transport.probe?.();
    this.wake?.();
  }

  private fail(message: string, kicked: boolean): void {
    this.closed = true;
    this.clearHelloTimeout();
    this.teardownGame();
    useRoomStore.getState().patch({
      status: 'error',
      error: message,
      connection: 'lost',
      joinStage: 'idle',
      joinHint: null,
    });
    if (kicked) writeGuestMemo(null);
    this.transport.destroy();
    this.removeListeners();
  }

  private teardownGame(): void {
    const store = useGame.getState();
    store.setInterceptor(null);
  }

  private removeListeners(): void {
    if (typeof window === 'undefined') return;
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.onVisible);
    window.removeEventListener('pageshow', this.onVisible);
  }

  /** Leave the room. `forget` also drops the persisted seat memo. */
  leave(forget = true): void {
    if (this.closed) return;
    this.closed = true;
    this.clearHelloTimeout();
    this.wake?.();
    this.conn?.close();
    this.conn = null;
    if (forget) writeGuestMemo(null);
    const store = useGame.getState();
    store.setInterceptor(null);
    store.quit();
    this.transport.destroy();
    this.removeListeners();
    useRoomStore.getState().reset();
  }
}

/** Seats to seed a reopened room, from the persisted game's remote players. */
export function seatsFromGame(
  game: GameState,
  remoteIds: string[]
): RemoteSeat[] {
  const ids = new Set(remoteIds);
  return game.players
    .filter((p): p is PlayerState => ids.has(p.id))
    .map(p => ({
      playerId: p.id,
      name: p.name,
      color: (PLAYER_COLORS.some(c => c.id === p.color)
        ? p.color
        : 'magenta') as PlayerColorId,
      status: 'disconnected' as const,
    }));
}
